import {
  Autocomplete,
  AutocompleteChangeReason,
  Grid,
  TextField,
} from "@mui/material";
import { FormsIds } from "../../Utils/Constants";
import { Vocabulary } from "../../Utils/Vocabulary";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useRef, useState } from "react";
import {
  CertificateExamValidationSchema,
  WorkPointModel,
} from "../../Utils/Models";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ro } from "date-fns/locale";
import { getData } from "../../Services/getData";
import { endpoints } from "../../Utils/UrlEnum";
import { AxiosResponse } from "axios";
import { getValueForAutocomplete, onFormError } from "../../Utils/Utils";
import { postData } from "../../Services/postData";
import { updateData } from "../../Services/updateData";

export interface CertificateExamFormProps {
  onSubmitSuccess?: () => void;
  selectedEvent?: any;
  selectedDate?: Date;
  selectedWorkPointId?: string;
}

export default function CertificateExamForm(props: CertificateExamFormProps) {
  const { onSubmitSuccess, selectedEvent, selectedDate, selectedWorkPointId } =
    props;
  const [workPoints, setWorkPoints] = useState<WorkPointModel[]>([]);
  const ref: any = useRef();

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      ...(selectedDate ? { examDate: selectedDate } : {}),
      ...(selectedEvent?.start ? { type: selectedEvent?.examType } : {}),
      ...(selectedWorkPointId ? { workPointId: selectedWorkPointId } : {}),
    },
    resolver: yupResolver(CertificateExamValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    context: undefined,
    shouldFocusError: true,
    shouldUnregister: false,
    criteriaMode: "firstError",
  });

  useEffect(() => {
    fetchWorkPoints();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      reset({
        ...selectedEvent,
        examDate: selectedEvent.start,
      });
    }
  }, [selectedEvent]);

  const fetchWorkPoints = async () => {
    try {
      const response: AxiosResponse = await getData(
        `${endpoints.workPoints.main}?page=0&perPage=100000`
      );
      if (response?.data) {
        setWorkPoints(response.data);
      }
    } catch (error) {
      console.error("Error fetching work points:", error);
    }
  };

  /**
   * Form submit handler: decides create or update
   * @param {object} model The model object to create or update.
   */
  const onSubmit = (model: any) => {
    model?.id ? updateCertificateExam(model) : createCertificateExam(model);
  };

  /**
   * Transform a model object into a DTO for creating a certificate exam.
   * @param {object} model The model object to transform.
   * @returns {object} The transformed DTO.
   */
  const transformModelToDto = (model: any) => {
    const { examDate, type, workPointId } = model;

    // Adjust for timezone offset to keep the intended date and set time to 8:00 AM
    // i set the time to 8, because i have the week view from 8 to 18, and the event needs to be seen
    const adjustedDate = new Date(
      examDate.getTime() - examDate.getTimezoneOffset() * 60000
    );
    adjustedDate.setHours(12, 0, 0, 0); // Set to 8:00 AM

    return {
      ...model,
      examDate: adjustedDate.toISOString(),
      type,
      workPointId,
    };
  };

  /**
   * Create a new certificate exam.
   * @param {object} model The model to use when creating the certificate exam.
   * @returns {Promise<void>}
   */
  const createCertificateExam = async (model: any) => {
    const url = endpoints.exams.certificateExams;
    const dto = transformModelToDto(model);

    try {
      const res = await postData(url, dto);
      if (!res) {
        console.error("Error response:", res);
        return;
      }

      if (onSubmitSuccess) {
        onSubmitSuccess();
      }

      reset();
    } catch (error) {
      console.error("Error creating certificate exam:", error);
    }
  };

  /**
   * Update an existing certificate exam.
   * @param {object} model The model to use when updating the certificate exam.
   * @returns {Promise<void>}
   */
  const updateCertificateExam = async (model: any) => {
    const url = `${endpoints.exams.certificateExams}/${model.id}`;
    const dto = transformModelToDto(model);

    try {
      await updateData(url, dto).then((res) => {
        if (onSubmitSuccess) {
          onSubmitSuccess();
        }
      });
    } catch (error) {
      console.error("Error updating certificate exam:", error);
    }
  };

  /**
   *
   * @returns
   */
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleSubmit(onSubmit, onFormError)(e);
      }}
      method="post"
      ref={ref}
      id={FormsIds.certificateExamForm}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={12}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
            <Controller
              control={control}
              name="examDate"
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <DatePicker
                  label={Vocabulary.examDate}
                  value={field.value ? new Date(field.value) : null}
                  onChange={(newDate) => field.onChange(newDate)}
                  views={["month", "year", "day"]}
                  slots={{ textField: TextField }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} sm={12} md={12}>
          <Controller
            name="workPointId"
            control={control}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Autocomplete
                size="small"
                disableClearable
                id="workPointId"
                fullWidth={true}
                freeSolo={false}
                disabled={selectedWorkPointId ? true : false}
                disablePortal
                noOptionsText={Vocabulary.noResultsFound}
                isOptionEqualToValue={(option: any, value: any) => {
                  return option?._id === value?._id || option?._id === value;
                }}
                getOptionLabel={(option: any) => option?.name || ""}
                options={workPoints || []}
                value={getValueForAutocomplete(workPoints, field.value)}
                onChange={(
                  e: any,
                  selectedWorkPoint: WorkPointModel,
                  reason: AutocompleteChangeReason,
                  details?: any
                ) => {
                  field.onChange(details?.option?._id);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    name="workPointId"
                    label={Vocabulary.workPoint}
                    variant="outlined"
                    size="medium"
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            )}
          />
        </Grid>
      </Grid>
    </form>
  );
}
